import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { iconMap } from '../../utils/iconUtils';
import { getCurrentTheme, setThemeMode, type ThemeMode } from '../../theme';

interface ThemeToggleProps {
  onThemeChange?: () => void;
}

/**
 * Theme Selector Component
 * Allows users to select from all available themes via a dropdown
 */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({ onThemeChange }) => {
  const [currentTheme, setCurrentTheme] = React.useState<ThemeMode>(
    getCurrentTheme()
  );
  const [isOpen, setIsOpen] = React.useState(false);

  const AVAILABLE_THEMES: ThemeMode[] = [
    'light',
    'dark',
    'liquid-glass',
    'dark-liquid-glass',
    'dark-purple-liquid-glass',
    'dark-cyan-neon',
    'dark-green-neon',
    'dark-synthwave',
    'soccer-green',
    'soccer-green-liquid-glass',
    'soccer-cyber',
  ];

  const getThemeInfo = (theme: ThemeMode) => {
    switch (theme) {
      case 'light':
        return { label: 'Light', icon: 'sun', color: '#FFC107' };
      case 'dark':
        return { label: 'Dark', icon: 'moon', color: '#4a4a4a' };
      case 'liquid-glass':
        return { label: 'Liquid Glass', icon: 'lightbulb', color: '#0066cc' };
      case 'dark-liquid-glass':
        return { label: 'Dark Glass', icon: 'lightbulb', color: '#6366f1' };
      case 'dark-purple-liquid-glass':
        return { label: 'Purple Cyber', icon: 'award', color: '#9D4EDD' };
      case 'dark-cyan-neon':
        return { label: 'Cyan Neon', icon: 'award', color: '#00FFFF' };
      case 'dark-green-neon':
        return { label: 'Green Neon', icon: 'award', color: '#00FF00' };
      case 'dark-synthwave':
        return { label: 'Synthwave', icon: 'award', color: '#FF6699' };
      case 'soccer-green':
        return { label: 'Soccer Green', icon: 'leaf', color: '#2d7a5a' };
      case 'soccer-green-liquid-glass':
        return { label: 'Soccer Glass', icon: 'leaf', color: '#3db867' };
      case 'soccer-cyber':
        return { label: 'Soccer Cyber', icon: 'leaf', color: '#00ff99' };
      default:
        return { label: 'Dark', icon: 'moon', color: '#4a4a4a' };
    }
  };

  const handleThemeSelect = (theme: ThemeMode) => {
    setThemeMode(theme);
    setCurrentTheme(theme);
    setIsOpen(false);
    onThemeChange?.();
  };

  const themeInfo = getThemeInfo(currentTheme);

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <button
        className="header-stat-item theme-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        title="Select a theme"
        aria-label={`Theme: ${themeInfo.label}. Click to open theme selector`}
        style={{ width: '100%', justifyContent: 'space-between' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="header-stat-icon">
            <FontAwesomeIcon icon={iconMap[themeInfo.icon]} style={{ color: themeInfo.color }} />
          </span>
          <div className="header-stat-text" style={{ margin: 0 }}>
            <span className="header-stat-label">Theme</span>
            <strong>{themeInfo.label}</strong>
          </div>
        </div>
        <span style={{ fontSize: '0.8em' }}>
          <FontAwesomeIcon icon={iconMap['chevron-down']} />
        </span>
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '8px',
            backgroundColor: 'var(--primary-color)',
            border: '2px solid rgba(var(--accent-color-rgb), 0.3)',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            zIndex: 1000,
            maxHeight: '400px',
            overflowY: 'auto',
            backdropFilter: 'blur(10px)',
          }}
        >
          {AVAILABLE_THEMES.map((theme) => {
            const info = getThemeInfo(theme);
            const isSelected = theme === currentTheme;
            return (
              <button
                key={theme}
                onClick={() => handleThemeSelect(theme)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: isSelected ? 'rgba(var(--accent-color-rgb), 0.2)' : 'transparent',
                  border: 'none',
                  borderLeft: isSelected ? '3px solid var(--accent-color)' : '3px solid transparent',
                  color: 'var(--text-color)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.15s ease',
                  fontSize: '0.95em',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(var(--accent-color-rgb), 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                  }
                }}
              >
                <FontAwesomeIcon icon={iconMap[info.icon]} style={{ color: info.color, width: '16px' }} />
                <span style={{ flex: 1 }}>{info.label}</span>
                {isSelected && (
                  <FontAwesomeIcon icon={iconMap['check']} style={{ color: 'var(--accent-color)', width: '16px' }} />
                )}
              </button>
            );
          })}
        </div>
      )}

      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};
